import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { X, Send, Loader2, ArrowRight, Sparkles } from "lucide-react";

const STORAGE_KEY = "durga_chat_session";

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chips, setChips] = useState([]);
  const [sessionId] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return saved;
      const id = `s_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(STORAGE_KEY, id);
      return id;
    } catch { return `s_${Math.random().toString(36).slice(2, 10)}`; }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      api.get("/chatbot/suggestions").then((r) => {
        setMessages([{ role: "assistant", text: r.data.greeting, links: [] }]);
        setChips(r.data.chips || []);
      }).catch(() => {
        setMessages([{ role: "assistant", text: "Hi! I'm ScrewBot. How can I help you today?", links: [] }]);
      });
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setInput("");
    setChips([]);
    setLoading(true);
    try {
      const r = await api.post("/chatbot/query", { message, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply, links: r.data.links || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble right now. Please try again — or use the contact form.", links: [{ title: "Contact Us", url: "/contact", type: "page" }] }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open ScrewBot AI assistant"
          data-testid="chatbot-toggle"
          className="fixed bottom-24 right-6 z-40 group"
        >
          <span className="absolute inset-0 rounded-full bg-[#C1121F] animate-pulse-ring" aria-hidden="true" />
          <span className="relative w-14 h-14 rounded-full bg-[#1C1C1C] border-2 border-[#C1121F] flex items-center justify-center shadow-[0_0_24px_rgba(193,18,31,0.45)] group-hover:scale-110 transition-transform overflow-hidden">
            <img src="/img/chatbot-icon.png" alt="ScrewBot AI" className="w-12 h-12 object-contain" />
          </span>
          <span className="absolute -top-1 -right-1 bg-[#C1121F] text-white text-[10px] font-heading font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wider">AI</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[calc(100vw-2rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-3rem)] bg-white border border-[#1C1C1C]/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] flex flex-col"
          data-testid="chatbot-panel"
        >
          {/* Header */}
          <div className="bg-[#1C1C1C] text-white p-4 flex items-center justify-between border-b-2 border-[#C1121F]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0A0A0A] rounded-full flex items-center justify-center overflow-hidden border border-[#C1121F]">
                <img src="/img/chatbot-icon.png" alt="" className="w-9 h-9 object-contain" />
              </div>
              <div>
                <div className="font-heading font-bold text-sm flex items-center gap-2">
                  ScrewBot AI <Sparkles size={12} className="text-[#C1121F]" />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 font-body">Durga Auto · Online</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chatbot-close" className="p-2 text-white/70 hover:text-white">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F5F5F5]" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <Message key={i} message={m} onLinkClick={() => setOpen(false)} />
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm font-body text-[#4B5563]">
                <Loader2 size={14} className="animate-spin text-[#C1121F]" /> ScrewBot is searching the catalog…
              </div>
            )}
            {chips.length > 0 && !loading && (
              <div className="flex flex-wrap gap-2 pt-2" data-testid="chatbot-chips">
                {chips.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => send(c)}
                    className="text-xs font-body bg-white border border-[#E5E7EB] hover:border-[#C1121F] hover:text-[#C1121F] px-3 py-2 transition-colors"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="bg-white border-t border-[#E5E7EB] p-3 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about products, specs, certifications…"
              className="flex-1 bg-transparent outline-none text-sm font-body text-[#1C1C1C] placeholder:text-[#A3A3A3] px-2"
              data-testid="chatbot-input"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 bg-[#C1121F] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#A00F1A] transition-colors"
              data-testid="chatbot-send"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </form>
          <div className="bg-white border-t border-[#F5F5F5] px-4 py-2 text-[10px] uppercase tracking-wider text-[#A3A3A3] font-body">
            Powered by ScrewBot · Catalog-aware AI
          </div>
        </div>
      )}
    </>
  );
}

function Message({ message, onLinkClick }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${isUser ? "bg-[#1C1C1C] text-white" : "bg-white border border-[#E5E7EB] text-[#1C1C1C]"} px-4 py-3 rounded-sm`}>
        <p className="font-body text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
        {!isUser && message.links?.length > 0 && (
          <div className="mt-3 space-y-2">
            {message.links.map((l, i) => (
              <Link
                key={i}
                to={l.url}
                onClick={onLinkClick}
                className="flex items-center justify-between gap-3 text-xs font-heading font-semibold border border-[#E5E7EB] hover:border-[#C1121F] hover:text-[#C1121F] px-3 py-2 transition-colors group"
                data-testid="chatbot-link"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="chip bg-[#1C1C1C] text-white text-[9px] !px-2 !py-0.5">{l.type || "link"}</span>
                  <span className="truncate">{l.title}</span>
                </div>
                <ArrowRight size={12} className="flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
